<template>
  <div class="leaderboards-page relative w-full flex-col items-center text-white bg-[#0f0f0f] mt-[20px] pt-[20px] lg:pt-[30px] pb-[80px]">

    <!-- ── Coming Soon Overlay (set comingSoon: false in data() to restore) ── -->
    <div v-if="comingSoon" class="relative z-10 flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
      <!-- Glow blobs -->
      <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div class="absolute top-[10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-[#a855f7] opacity-[0.15] blur-[140px]"></div>
        <div class="absolute bottom-[10%] right-[-5%] w-[400px] h-[400px] rounded-full bg-[#00e5ff] opacity-[0.12] blur-[140px]"></div>
      </div>
      <div class="relative z-10 flex flex-col items-center gap-6">
        <!-- Icon -->
        <div class="w-24 h-24 rounded-full bg-gradient-to-br from-[#7c3aed]/30 to-[#00e5ff]/20 border border-[#00e5ff]/30 flex items-center justify-center shadow-[0_0_40px_rgba(0,229,255,0.2)]">
          <svg class="w-12 h-12 text-[#00e5ff]" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
          </svg>
        </div>
        <!-- Title -->
        <h1 class="text-4xl sm:text-5xl lg:text-6xl font-black tracking-widest uppercase bg-gradient-to-r from-[#c6f605] via-[#00e5ff] to-[#a855f7] bg-clip-text text-transparent leading-tight">
          {{ $t('Common.ComingSoon') }}
        </h1>
        <!-- Subtitle -->
        <p class="text-white/50 text-lg sm:text-xl font-semibold max-w-md">
          {{ $t('Common.WillBeAvailableSoon') }}
        </p>
        <!-- Decorative divider -->
        <div class="w-32 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#00e5ff] to-transparent"></div>
        <p class="text-white/30 text-sm">{{ $t('LeaderboardsPage.Leaderboard') }}</p>
      </div>
    </div>

    <!-- ── Existing Content (hidden while comingSoon is true) ── -->
    <div v-else class="w-full">
    <!-- Background glow blobs (subtle) -->
    <div class="pointer-events-none absolute inset-0 z-0">
      <div class="absolute top-[3%] left-[-8%] w-[400px] h-[400px] rounded-full bg-[#a855f7] opacity-[0.12] blur-[120px]"></div>
      <div class="absolute top-[12%] right-[2%] w-[360px] h-[360px] rounded-full bg-[#00e5ff] opacity-[0.08] blur-[120px]"></div>
      <div class="absolute top-[35%] left-[18%] w-[300px] h-[300px] rounded-full bg-[#7c3aed] opacity-[0.07] blur-[110px]"></div>
      <div class="absolute top-[52%] right-[-4%] w-[440px] h-[440px] rounded-full bg-[#a855f7] opacity-[0.09] blur-[140px]"></div>
      <div class="absolute top-[65%] left-[-2%] w-[380px] h-[380px] rounded-full bg-[#6d28d9] opacity-[0.08] blur-[130px]"></div>
      <div class="absolute top-[78%] right-[15%] w-[320px] h-[320px] rounded-full bg-[#00e5ff] opacity-[0.06] blur-[120px]"></div>
    </div>
    <div class="relative z-10 w-full h-full flex flex-col items-center justify-start">

      <!-- ── Header Banner ── -->
      <section class="w-full max-w-[1200px] mx-auto px-4 pt-10 pb-4">
        <div class="relative w-full h-auto min-h-[90px] md:h-[110px] lg:h-[120px] py-6 md:py-0 overflow-hidden rounded-[20px] flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0 bg-[#0d1225] border border-[#1e2535]">
          <!-- Subtle dark overlay to deepen the navy -->
          <div class="absolute inset-0 bg-[#060b18]/20 z-[1] pointer-events-none"></div>
          <!-- Left gradient for text readability -->
          <div class="absolute inset-0 bg-gradient-to-r from-[#060b18]/85 via-[#060b18]/30 to-transparent z-[1] pointer-events-none"></div>

          <!-- Left: label + title -->
          <div class="relative z-[2] flex flex-col items-center md:items-start text-center md:text-left gap-1.5 px-4 md:pl-8 flex-1 min-w-0">
            <div class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/60">
              <span class="text-[#00e5ff] tracking-[0.5em]">{{ $t('LeaderboardsPage.WeeklyRankings') }}</span>
            </div>
            <h1 class="font-montserrat font-extrabold text-[32px] md:text-[48px] leading-[1.1] md:leading-[56px] tracking-tight md:tracking-[-0.96px] align-middle whitespace-normal md:whitespace-nowrap">
              <span class="text-[#c6f605]">{{ currentPrizeTitle }}</span>
              <span class="text-white uppercase">&nbsp;{{ $t('LeaderboardsPage.Leaderboard') }}</span>
            </h1>
          </div>

          <!-- Right: Timer blocks -->
          <div class="relative z-[2] px-4 md:pr-8 shrink-0 w-full md:w-auto flex justify-center md:justify-end">
            <div v-if="!isLeaderboardPaused && timer !== 'Leaderboard ended'" class="flex gap-4">
              <div
                v-for="(segment, i) in timeSegments"
                :key="i"
                class="flex flex-col items-center gap-1.5"
              >
                <div class="flex items-center justify-center bg-[#0b1829] border border-[#00e5ff]/60 rounded-xl w-[40px] h-[40px] md:w-[58px] md:h-[58px] shadow-[0_0_8px_#00e5ff44]">
                  <span class="text-xl md:text-3xl font-black text-[#00e5ff] leading-none">
                    {{ (timer?.split(" ")[i]?.slice(0, -1) || "0").padStart(2, "0") }}
                  </span>
                </div>
                <span class="text-[10px] font-bold uppercase tracking-widest text-white/50">
                  {{ segment.label }}
                </span>
              </div>
            </div>
            <div v-else-if="!isLeaderboardPaused" class="text-white/50 text-sm font-semibold">
              {{ $t('LeaderboardsPage.LeaderboardEnded') }}
            </div>
          </div>
        </div>
      </section>

      <!-- ── Loading Spinner ── -->
      <div v-if="loading" class="flex justify-center items-center min-h-[400px] w-full">
        <div class="twist-spinner"></div>
      </div>

      <template v-else>
        <!-- ── Paused State ── -->
        <div v-if="isLeaderboardPaused" class="flex flex-col items-center justify-center min-h-[400px] text-center px-4 w-full">
          <h2 class="text-3xl sm:text-4xl lg:text-5xl font-black font-orbitron tracking-widest bg-gradient-to-r from-[#c6f605] to-[#00e5ff] text-transparent bg-clip-text mb-6">
            {{ $t('LeaderboardsPage.UnavailableForNow') }}
          </h2>
          <p class="text-white/60 text-xl sm:text-2xl font-bold">
            {{ $t('LeaderboardsPage.StayTuned') }}
          </p>
        </div>

        <template v-else>
          <!-- ── Podium Section ── -->
          <div class="relative w-full max-w-[1200px] mx-auto px-4 pt-12 pb-0">
            <div class="flex flex-col sm:flex-row items-center sm:items-end justify-center w-full gap-8 sm:gap-12">

              <!-- 2nd Place -->
              <div class="order-2 sm:order-1 w-full max-w-[280px] sm:max-w-none flex-[1.2] group">
                <div class="flex flex-col items-center transition-transform duration-300 ease-in-out group-hover:-translate-y-1.5">
                  <div class="relative z-20 mb-5">
                    <div class="w-[80px] h-[80px] rounded-full overflow-hidden bg-[#0c1220] border-[4px] border-[#AAB3D0]">
                      <img src="@/assets/profile-fallback.avif" alt="avatar" class="w-full h-full object-cover opacity-70" />
                    </div>
                    <div class="absolute bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-black text-sm font-bold bg-[#AAB3D0]">2</div>
                  </div>
                  <div class="w-full flex flex-col items-center text-center overflow-hidden h-[250px] rounded-t-[18px] border-t border-x border-white/[0.06] bg-gradient-to-b from-[rgba(20,24,38,0.95)] to-[rgba(15,18,30,0.95)] pt-20 pb-6 px-5">
                    <div class="truncate w-full text-2xl font-bold text-[#E8EAF4] mb-1.5 leading-tight">
                      {{ top3[1]?.username ? getMaskedUsername(top3[1].username) : '—' }}
                    </div>
                    <div class="text-sm text-white/65 mb-4">
                      {{ getCurrencyFormatted(top3[1]?.wagered) }} {{ $t('LeaderboardsPage.Wagered') }}
                    </div>
                    <div class="flex items-center justify-center font-semibold shrink-0 w-full max-w-[160px] h-9 rounded-full bg-white/5 text-[#AAB3D0] text-sm tracking-widest">
                      {{ getRewardValue(1) }} {{ $t('LeaderboardsPage.Prize') }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- 1st Place (center, taller + wider) -->
              <div class="order-1 sm:order-2 w-full max-w-[280px] sm:max-w-none flex-[1.1] z-10 group">
                <div class="flex flex-col items-center transition-transform duration-300 ease-in-out group-hover:-translate-y-1.5">
                  <div class="relative z-20 mb-5">
                    <div class="w-[80px] h-[80px] sm:w-[100px] sm:h-[100px] rounded-full overflow-hidden bg-[#091520] border-[4px] border-[#00E5FF] shadow-[0_0_20px_rgba(0,229,255,0.5)]">
                      <img src="@/assets/profile-fallback.avif" alt="avatar" class="w-full h-full object-cover opacity-70" />
                    </div>
                    <div class="absolute bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center text-black text-sm sm:text-base font-bold bg-[#00DFFF] shadow-[0_0_10px_rgba(0,229,255,0.8]">1</div>
                  </div>
                  <div class="w-full flex flex-col items-center text-center overflow-hidden h-[250px] sm:h-[330px] rounded-t-[18px] sm:rounded-t-[22px] border-t border-x border-[#00ffff]/50 bg-gradient-to-t from-[rgba(0,240,255,0.1)] to-[rgba(15,18,30,0.98)] pt-20 sm:pt-24 pb-6 px-5 sm:px-6">
                    <div class="truncate w-full text-2xl sm:text-3xl font-extrabold text-[#00E5FF] mb-1.5 leading-tight">
                      {{ top3[0]?.username ? getMaskedUsername(top3[0].username) : '—' }}
                    </div>
                    <div class="text-sm text-white/65 mb-4 sm:mb-5">
                      {{ getCurrencyFormatted(top3[0]?.wagered) }} {{ $t('LeaderboardsPage.Wagered') }}
                    </div>
                    <div class="flex items-center justify-center shrink-0 w-full max-w-[160px] sm:max-w-[220px] h-9 sm:h-11 rounded-full bg-[#1AD9F3] text-sm sm:text-base font-extrabold tracking-widest text-black shadow-[0_0_18px_4px_rgba(26,217,243,0.55)]">
                      {{ getRewardValue(0) }} {{ $t('LeaderboardsPage.Prize') }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- 3rd Place -->
              <div class="order-3 w-full max-w-[280px] sm:max-w-none flex-[1.2] group">
                <div class="flex flex-col items-center transition-transform duration-300 ease-in-out group-hover:-translate-y-1.5">
                  <div class="relative z-20 mb-5">
                    <div class="w-[80px] h-[80px] rounded-full overflow-hidden bg-[#0c1220] border-[4px] border-[#FF9C3B]">
                      <img src="@/assets/profile-fallback.avif" alt="avatar" class="w-full h-full object-cover opacity-70" />
                    </div>
                    <div class="absolute bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-black text-sm font-bold bg-[#FF9C3B]">3</div>
                  </div>
                  <div class="w-full flex flex-col items-center text-center overflow-hidden h-[250px] rounded-t-[18px] border-t border-x border-white/[0.06] bg-gradient-to-b from-[rgba(20,24,38,0.95)] to-[rgba(15,18,30,0.95)] pt-20 pb-6 px-5">
                    <div class="truncate w-full text-2xl font-bold text-[#E8EAF4] mb-1.5 leading-tight">
                      {{ top3[2]?.username ? getMaskedUsername(top3[2].username) : '—' }}
                    </div>
                    <div class="text-sm text-white/65 mb-4">
                      {{ getCurrencyFormatted(top3[2]?.wagered) }} {{ $t('LeaderboardsPage.Wagered') }}
                    </div>
                    <div class="flex items-center justify-center font-semibold shrink-0 w-full max-w-[160px] h-9 rounded-full bg-white/5 text-[#FF9C3B] text-sm tracking-widest">
                      {{ getRewardValue(2) }} {{ $t('LeaderboardsPage.Prize') }}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- ── Tab Buttons ── -->
          <div class="flex flex-col sm:flex-row items-center gap-4 mb-8 mt-6 w-full sm:w-auto px-4 sm:px-0">
            <button
              @click="previousLeaderboard = false; changeLeaderboard();"
              :class="[
                'w-full sm:w-auto px-6 py-2.5 rounded-lg text-sm font-bold tracking-wide transition-all border',
                !previousLeaderboard
                  ? 'bg-[#00e5ff] text-black border-[#00e5ff] shadow-[0_0_16px_rgba(0,229,255,0.4)]'
                  : 'bg-transparent text-white/60 border-white/20 hover:border-white/40 hover:text-white/80',
              ]"
            >
              {{ $t('LeaderboardsPage.CurrentLeaderboard') }}
            </button>
            <button
              @click="previousLeaderboard = true; changeLeaderboard();"
              :class="[
                'w-full sm:w-auto px-6 py-2.5 rounded-lg text-sm font-bold tracking-wide transition-all border',
                previousLeaderboard
                  ? 'bg-[#00e5ff] text-black border-[#00e5ff] shadow-[0_0_16px_rgba(0,229,255,0.4)]'
                  : 'bg-transparent text-white/60 border-white/20 hover:border-white/40 hover:text-white/80',
              ]"
            >
              {{ $t('LeaderboardsPage.PreviousLeaderboard') }}
            </button>
          </div>

          <!-- ── Table ── -->
          <div class="w-full max-w-[1200px] mx-auto px-4">
            <div class="w-full rounded-2xl bg-[#0d1117] border border-white/[0.07] overflow-hidden">
              <div class="w-full overflow-x-auto">
                <div class="min-w-[580px]">

                  <!-- Column headers -->
                  <div class="grid grid-cols-[100px_1fr_200px_140px] px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-white/30 border-b border-white/[0.06]">
                    <div>{{ $t('LeaderboardsPage.Rank') }}</div>
                    <div>{{ $t('LeaderboardsPage.Player') }}</div>
                    <div>{{ $t('LeaderboardsPage.WageredAmount') }}</div>
                    <div class="text-right">{{ $t('LeaderboardsPage.Prize') }}</div>
                  </div>

                  <!-- No data -->
                  <div v-if="topRest.length === 0" class="text-center text-white/30 py-12 text-sm">
                    {{ $t('LeaderboardsPage.NoDataYet') }}
                  </div>

                  <!-- Rows -->
                  <template v-else>
                  <div
                    v-for="(entry, index) in pagedTopRest"
                    :key="index"
                    class="grid grid-cols-[100px_1fr_200px_140px] px-8 py-5 border-b border-white/[0.05] transition-colors last:border-b-0 items-center"
                  >
                    <div class="font-bold text-white text-xl tabular-nums">
                      {{ String((currentPage - 1) * pageSize + index + 4).padStart(2, "0") }}
                    </div>
                    <div class="flex items-center gap-4 pr-4 min-w-0">
                      <div class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black shrink-0 bg-[#00e5ff] text-black tracking-tight">
                        {{ getInitials(entry.username) }}
                      </div>
                      <span class="font-semibold text-white text-base truncate">{{ getMaskedUsername(entry.username) }}</span>
                    </div>
                    <div class="text-left text-white text-base tabular-nums">
                      {{ getCurrencyFormatted(entry.wagered) }}
                    </div>
                    <div class="text-right text-[#e879f9] font-bold text-base tabular-nums">
                      {{ getRewardValue((currentPage - 1) * pageSize + index + 3) }}
                    </div>
                  </div>
                  </template>

                </div>
              </div>

              <!-- Table Footer: showing + pagination -->
              <div class="flex items-center justify-between px-8 py-5 border-t border-white/[0.06]">
                <span class="text-[11px] font-bold uppercase tracking-widest text-white/30">
                  {{ $t('LeaderboardsPage.Showing') }} {{ (currentPage - 1) * pageSize + 1 }}-{{ pageEnd }} {{ $t('LeaderboardsPage.Of') }} {{ topRest.length }} {{ $t('LeaderboardsPage.Players') }}
                </span>
                <div v-if="totalPages > 1" class="flex items-center gap-1.5">
                  <button
                    @click="goToPage(currentPage - 1)"
                    :disabled="currentPage === 1"
                    class="w-8 h-8 rounded-md flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-sm"
                  >‹</button>
                  <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="[
                      'w-8 h-8 rounded-md flex items-center justify-center text-sm font-bold transition-all',
                      page === currentPage
                        ? 'bg-[#00e5ff] text-black shadow-[0_0_10px_rgba(0,229,255,0.4)]'
                        : 'text-white/40 hover:text-white hover:bg-white/10',
                    ]"
                  >{{ page }}</button>
                  <button
                    @click="goToPage(currentPage + 1)"
                    :disabled="currentPage === totalPages"
                    class="w-8 h-8 rounded-md flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-sm"
                  >›</button>
                </div>
              </div>
            </div>

            <!-- Mobile scroll hint -->
            <div class="flex md:hidden items-center justify-center gap-3 text-white/30 font-semibold mt-4 text-xs">
              <div class="relative w-12 h-5 rounded-full bg-white/5 overflow-hidden shrink-0">
                <div class="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white/30 animate-scroll-dot"></div>
              </div>
              <span>{{ $t('LeaderboardsPage.ScrollToSeeMore') }}</span>
            </div>
          </div>

          <!-- ── How It Works (hidden) ── -->

        </template>
      </template>

    </div>
  </div>
  </div><!-- end v-else -->
</template>


<script>
import axios from "axios";
import moment from "moment-timezone";
import { defineComponent } from "vue";

export default defineComponent({
  data() {
    return {
      // Set to false to restore the leaderboard page
      comingSoon: true,

      // Main state
      currentLeaderboard: "default",
      loading: false,
      selectedLeaderboard: 1,
      showDisclaimer: false,

      // Leaderboard data
      leaderboardData: {},
      leaderboardPrevData: {},
      top3: [],
      topRest: [],
      topForMobile: [],
      previousLeaderboard: false,

      // Pagination
      currentPage: 1,
      pageSize: 7,

      // Timer
      timer: "0d 0h 0m 0s",
      timerInterval: null,
      timeSegments: [
        { label: "DAYS" },
        { label: "HOURS" },
        { label: "MINS" },
        { label: "SECS" },
      ],
      startDate: null,
      endDate: null,
      notFormattedEndDate: null,

      isLeaderboardPaused: false,
      // Prizes
      animatedTotalPrize: 0,
      prizes: {},
    };
  },

  computed: {
    currentPrizeTitle() {
      const val = this.animatedTotalPrize;
      if (val >= 1000) return `$${Math.round(val / 1000)}k`;
      return `$${Math.round(val)}`;
    },
    pagedTopRest() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.topRest.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.topRest.length / this.pageSize);
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.topRest.length);
    },
  },

  methods: {
    // Fetching leaderboard data
    async fetchLeaderboard() {
      if (this.comingSoon) return;
      this.loading = true;
      this.resetLeaderboardState();

      try {
        const baseUrl = process.env.VUE_APP_BASE_API;
        const apiKey = process.env.VUE_APP_X_API_KEY;

        const currentRes = await axios.get(
          `${baseUrl}/api/leaderboard/${this.currentLeaderboard}`,
          { headers: { "x-api-key": apiKey } }
        );

        this.leaderboardData = currentRes.data || {};
        this.startDate = moment(this.leaderboardData.startDate).format("MMMM DD, YYYY");
        this.endDate = moment(this.leaderboardData.endDate).format("MMMM DD, YYYY");
        this.notFormattedEndDate = this.leaderboardData.endDate;

        this.leaderboardData = this.normalizeLeaderboardData(this.leaderboardData);
        this.leaderboardPrevData = this.normalizeLeaderboardData(
          this.leaderboardPrevData
        );

        this.changeLeaderboard();
        this.startTimer();
        this.animateValues();
      } catch (error) {
        console.error("Leaderboard fetch error:", error);
      } finally {
        this.loading = false;

        // If paused, clear any data that might have been fetched
        if (this.isLeaderboardPaused) {
          this.top3 = [];
          this.topRest = [];
          this.topForMobile = [];
          this.animatedTotalPrize = 0;
        }
      }
    },

    resetLeaderboardState() {
      this.leaderboardData = {};
      this.leaderboardPrevData = {};
      this.top3 = [];
      this.topRest = [];
      this.topForMobile = [];
    },

    normalizeLeaderboardData(data) {
      if (!data?.leaderboard) return { leaderboard: [] };
      return {
        startDate: data.startDate,
        endDate: data.endDate,
        leaderboard: data.leaderboard.map((entry) => ({
          username: this.getUsername(entry),
          wagered: this.getWageredAmount(entry),
        })),
      };
    },

    getUsername(entry) {
      return entry.user_name || entry.username || "";
    },

    getWageredAmount(entry) {
      const raw = entry.wagered || entry.total_wagered || "";
      const clean = raw.toString().replace(/[$,]/g, "");
      const value = parseFloat(clean);
      return isNaN(value) ? 0 : value;
    },

    changeLeaderboard() {
      const isPrev = this.previousLeaderboard;
      const data = isPrev
        ? this.leaderboardPrevData.leaderboard
        : this.leaderboardData.leaderboard;
      const source = isPrev ? this.leaderboardPrevData : this.leaderboardData;

      this.startDate = moment(source.startDate).format("MMMM DD, YYYY");
      this.endDate = moment(source.endDate).format("MMMM DD, YYYY");
      this.notFormattedEndDate = source.endDate;

      const sliceLimit = 20;

      if (this.isLeaderboardPaused) {
        this.top3 = [];
        this.topRest = [];
        this.topForMobile = [];
        return;
      }

      this.top3 = data?.slice(0, 3) || [];
      this.topRest = data?.slice(3, sliceLimit) || [];
      this.topForMobile = data?.slice(0, sliceLimit) || [];
      this.currentPage = 1;
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    toggleLeaderboard(type) {
      if (this.currentLeaderboard !== type) {
        this.currentLeaderboard = type;
        this.changeLeaderboard();
      }
    },

    getRewardValue(rank) {
      const key = `VUE_APP_${this.currentLeaderboard.toUpperCase()}_${
        this.previousLeaderboard ? "PREVIOUS" : "CURRENT"
      }_LEADERBOARD_REWARDS`;
      const rewards = JSON.parse(process.env[key] || "[]");

      const value = rank >= rewards.length ? rewards[rewards.length - 1] : rewards[rank];
      if (value == null) return this.currentLeaderboard === "raingg" ? "0c" : "$0";
      return this.currentLeaderboard === "raingg" ? `${value}c` : `$${value}`;
    },

    startTimer() {
      clearInterval(this.timerInterval);
      if (this.previousLeaderboard || !this.notFormattedEndDate) {
        this.setZeroTimer();
        return;
      }

      const periodKey = `VUE_APP_${this.currentLeaderboard.toUpperCase()}_TIMER_PERIOD`;
      const period = process.env[periodKey] || "";
      const getNextTargetDate = () => {
        const now = moment().tz("America/New_York");
        let target = moment
          .utc(this.notFormattedEndDate)
          .tz("America/New_York")
          .subtract(1, "s");
        if (now.isAfter(target)) {
          if (period === "week") target.add(1, "week");
          else if (period === "month") target.add(1, "month");
          else if (period === "custom") {
            const days = parseInt(
              process.env[`${this.currentLeaderboard.toUpperCase()}_CUSTOM_DAYS`] || "7"
            );
            target.add(days, "days");
          }
        }
        return target;
      };

      const targetDate = getNextTargetDate();
      const update = () => {
        const now = moment().tz("America/New_York");
        const dist = targetDate.diff(now);
        if (dist >= 0) {
          const dur = moment.duration(dist);
          this.timer = `${Math.floor(
            dur.asDays()
          )}d ${dur.hours()}h ${dur.minutes()}m ${dur.seconds()}s`;
          this.timeSegments.forEach((s, i) => {
            const val = this.timer.split(" ")[i];
            s.value = val ? val.slice(0, -1) : "0";
          });
        } else {
          this.setZeroTimer();
          clearInterval(this.timerInterval);
        }
      };

      update();
      this.timerInterval = setInterval(update, 1000);
    },

    setZeroTimer() {
      this.timer = "0d 0h 0m 0s";
      this.timeSegments.forEach((segment) => (segment.value = "0"));
    },

    animateValues() {
      const key = `VUE_APP_${this.currentLeaderboard.toUpperCase()}_${
        this.previousLeaderboard ? "PREVIOUS" : "CURRENT"
      }_LEADERBOARD_REWARDS`;
      const rewards = JSON.parse(process.env[key] || "[]");
      const total = rewards.reduce((sum, val) => sum + val, 0);
      this.animateNumber("animatedTotalPrize", total);
    },

    animateNumber(variable, target) {
      let current = this[variable] || 0;
      if (current === target) return;

      const step = Math.max(1, Math.abs(target - current) / 20);
      const isDown = current > target;

      const interval = setInterval(() => {
        current += isDown ? -step : step;
        if ((isDown && current <= target) || (!isDown && current >= target)) {
          current = target;
          clearInterval(interval);
        }
        this.$nextTick(() => {
          this[variable] = parseFloat(current.toFixed(2));
        });
      }, 50);
    },

    getMaskedUsername(username) {
      if (!username || typeof username !== "string") return "";
      return username.length <= 2
        ? username
        : `${username[0]}${"*".repeat(username.length - 2)}${username.at(-1)}`;
    },

    getCurrencyFormatted(value) {
      if (value === undefined || isNaN(value)) value = 0;
      const formatted = Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return this.currentLeaderboard === "raingg" ? `${formatted}c` : `$${formatted}`;
    },

    getOrdinalSuffix(n) {
      const s = ["th", "st", "nd", "rd"],
        v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },

    getInitials(username) {
      if (!username || typeof username !== "string") return "??";
      const masked = this.getMaskedUsername(username);
      return masked.slice(0, 2).toUpperCase();
    },

    getAvatarStyle(index) {
      const palettes = [
        { bg: "rgba(59,130,246,0.2)", color: "#60a5fa" },
        { bg: "rgba(16,185,129,0.2)", color: "#34d399" },
        { bg: "rgba(249,115,22,0.2)", color: "#fb923c" },
        { bg: "rgba(168,85,247,0.2)", color: "#c084fc" },
        { bg: "rgba(236,72,153,0.2)", color: "#f472b6" },
        { bg: "rgba(20,184,166,0.2)", color: "#2dd4bf" },
        { bg: "rgba(234,179,8,0.2)", color: "#facc15" },
      ];
      const p = palettes[index % palettes.length];
      return { background: p.bg, color: p.color, border: `1px solid ${p.color}40` };
    },

    toggleDisclaimer() {
      this.showDisclaimer = !this.showDisclaimer;
    },
  },

  mounted() {
    this.fetchLeaderboard();
    this.$emit("sections-ready");
  },
});
</script>

<style scoped>
.leaderboards-page {
  font-family: 'Montserrat', sans-serif;
}

.timer-glow {
  text-shadow: 0 0 8px rgba(0, 229, 255, 0.8), 0 0 20px rgba(0, 229, 255, 0.4);
  -webkit-text-stroke: 1px rgba(0, 229, 255, 0.6);
}

/* Smooth fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.loader {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid white;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes scroll-dot {
  0% {
    transform: translate(20%, -50%);
    opacity: 0.8;
  }
  50% {
    transform: translate(270%, -50%);
    opacity: 1;
  }
  100% {
    transform: translate(20%, -50%);
    opacity: 0.8;
  }
}

.animate-scroll-dot {
  animation: scroll-dot 5s infinite ease-in-out;
}

@keyframes twist-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.twist-spinner {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 3px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-small {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-big {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 4px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

</style>
